import React, { useMemo, useState } from 'react';
import { Alert, Button, Popconfirm, Select, Space, Switch, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

const { Text } = Typography;

export function LinkedAssistantsAddControl({
  linkedAssistants = [],
  assistants = [],
  onAdd,
  adding = false,
}) {
  const linkedIds = useMemo(
    () => new Set(linkedAssistants.map((a) => a.assistant_id)),
    [linkedAssistants],
  );
  const addOptions = assistants
    .filter((a) => !linkedIds.has(a.id))
    .map((a) => ({ value: a.id, label: a.name || `Assistant ${a.id}` }));

  const [pickId, setPickId] = useState(null);

  if (addOptions.length === 0) return null;

  return (
    <Space size="small" wrap>
      <Select
        placeholder="Add assistant"
        style={{ minWidth: 180 }}
        size="small"
        value={pickId}
        onChange={setPickId}
        options={addOptions}
        allowClear
      />
      <Button
        type="primary"
        size="small"
        icon={<PlusOutlined />}
        loading={adding}
        disabled={!pickId}
        onClick={async () => {
          if (!pickId) return;
          await onAdd?.(pickId);
          setPickId(null);
        }}
      >
        Add
      </Button>
    </Space>
  );
}

export default function LinkedAssistantsPanel({
  linkedAssistants = [],
  assistants = [],
  assistantsLoaded,
  onAdd,
  onToggle,
  onRemove,
  adding = false,
}) {
  if (!assistantsLoaded) {
    return <Text type="secondary">Loading assistants…</Text>;
  }

  if (linkedAssistants.length === 0 && assistants.length === 0) {
    return (
      <Alert
        type="info"
        showIcon
        message="No assistants yet"
        description="Create an assistant first, then link this tool."
      />
    );
  }

  return (
    <>
      {linkedAssistants.map((a) => (
        <div key={a.assistant_id} className="tools-linked-row">
          <div>
            <Text>{a.name || `Assistant ${a.assistant_id}`}</Text>
            <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
              {a.enabled ? 'Tools active' : 'Link paused'}
            </Text>
          </div>
          <Space>
            <Switch
              size="small"
              checked={Boolean(a.enabled)}
              onChange={(checked) => onToggle?.(a.assistant_id, checked)}
            />
            <Popconfirm
              title="Remove from this assistant?"
              onConfirm={() => onRemove?.(a.assistant_id)}
              okText="Remove"
              okButtonProps={{ danger: true }}
            >
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        </div>
      ))}
      {linkedAssistants.length > 0 && assistants.length > 0 && linkedAssistants.length >= assistants.length ? (
        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          All assistants in this scope are already linked.
        </Text>
      ) : null}
    </>
  );
}
